import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useState } from "react";
import {
  fetchCompanyFullProductData,
  fetchFullProductData,
} from "../api/fullProductData.api";
import {
  CreateProductPayload,
  UpdateProductPayload,
} from "../api/models/product.models";
import { createProduct, updateProduct } from "../api/products.api";

interface UseProductProps {
  companyId?: number;
}

const useProducts = ({ companyId }: UseProductProps) => {
  const queryClient = useQueryClient();
  const [ean, setEan] = useState<string>("");
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  const {
    data: productData,
    error: productError,
    isLoading: isLoadingProduct,
    isSuccess: isSuccessProduct,
  } = useQuery({
    queryKey: ["products", ean],
    queryFn: () =>
      companyId
        ? fetchCompanyFullProductData(
            bearerToken as string,
            companyId,
            [ean],
            true,
          )
        : fetchFullProductData(bearerToken as string, [ean]),
    enabled: !!ean && ean !== "",
  });

  const createNewAkeneoProduct = async (payload: CreateProductPayload) => {
    const result = await createProductMutation.mutateAsync(payload);
    return result;
  };

  const createProductMutation = useMutation({
    mutationFn: (payload: CreateProductPayload) =>
      createProduct(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create new product:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
      queryClient.invalidateQueries({ queryKey: ["searchProducts"] });
      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
    },
  });

  // Update existing simple package
  const updateExistingProduct = async (payload: UpdateProductPayload) => {
    const result = await updateProductMutation.mutateAsync(payload);
    return result;
  };

  const updateProductMutation = useMutation({
    mutationFn: (payload: UpdateProductPayload) =>
      updateProduct(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update product:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products"],
      });
      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
    },
  });

  const isUpdatingProduct = updateProductMutation.isPending;
  const isCreatingProduct = createProductMutation.isPending;

  return {
    productData,
    productError,
    searchEan: ean,
    setSearchEan: setEan,
    isLoadingProduct,
    isSuccessProduct,
    updateExistingProduct,
    isUpdatingProduct,
    createNewAkeneoProduct,
    isCreatingProduct,
  };
};

export default useProducts;
